import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";

const RequestsListScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const hasRefreshedRef = useRef(false);
  const [freshDataReady, setFreshDataReady] = useState(false);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { progress, isLoading: progressLoading, refreshProgress } = useProgressWithAutoRefresh();

  useEffect(() => {
    // Refresh progress on mount to ensure latest data (e.g., after flow navigation)
    // Wait for the refresh to complete before allowing redirect decisions
    if (!hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      console.log('🔄 [RequestsListScreen] Refreshing progress data on mount');
      refreshProgress(true).then(() => setFreshDataReady(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Wait for FRESH progress data before deciding to redirect
    if (!freshDataReady || progressLoading) return;

    const requests = progress?.active_requests || [];

    // Staff requested corrections while the doctor was sitting here: route to
    // /corrections (mirrors useStepRedirect's decision) — previously the fresh
    // status was fetched but ignored, so the doctor had to sign out/in.
    const needsCorrections = requests.find(
      (r) => r.status === 'changes_requested' && (r.corrections?.corrections?.length ?? 0) > 0
    );
    if (needsCorrections) {
      console.log('🔀 [RequestsListScreen] Corrections requested, redirecting to /corrections');
      navigate("/corrections", { replace: true });
      return;
    }

    if (requests.length === 0) {
      // No requests found - redirect to home
      console.log('🔀 [RequestsListScreen] No active requests, redirecting to home');
      navigate("/home", { replace: true });
    } else {
      setLoading(false);
    }
  }, [progress, progressLoading, freshDataReady, navigate]);

  if (loading || progressLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  const requests = progress?.active_requests || [];

  const getStatusColor = (status: string) => {
    if (status === 'pending' || status === 'pending_inspection' || status === 'pending_payment') return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    if (status === 'draft') return 'bg-blue-50 border-blue-200 text-blue-800';
    if (status === 'in_review' || status === 'submitted') return 'bg-blue-50 border-blue-200 text-blue-800';
    if (status === 'preliminary_approved') return 'bg-teal-50 border-teal-200 text-teal-800';
    if (status === 'approved' || status === 'completed') return 'bg-green-50 border-green-200 text-green-800';
    if (status === 'rejected' || status === 'cancelled') return 'bg-red-50 border-red-200 text-red-800';
    return 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'pending' || status === 'pending_inspection' || status === 'pending_payment') return '⏳';
    if (status === 'draft') return '📝';
    if (status === 'in_review' || status === 'submitted') return '🔍';
    if (status === 'preliminary_approved') return '☑️';
    if (status === 'approved' || status === 'completed') return '✅';
    if (status === 'rejected' || status === 'cancelled') return '❌';
    return '📄';
  };

  const getRequestTypeLabel = (type: string) => {
    if (type === 'registration') return t("clinic_registration");
    if (type === 'onboarding') return t("clinic_onboarding");
    if (type === 'closure') return t("clinic_closure") || "Clinic Closure";
    return type;
  };

  const navigateToRequestDetail = (request: any) => {
    if (request.request_type === 'closure') {
      navigate(`/closure-request/${request.request_id}`, {
        state: { requestType: request.request_type }
      });
    } else {
      navigate(`/request/${request.request_id}`, {
        state: { requestType: request.request_type }
      });
    }
  };

  return (
    <ResponsiveFormLayout
      showLanguageToggle={true}
      icon="📋"
      iconSize="w-24 h-24 text-6xl"
      title={t("my_requests")}
      subtitle={`${requests.length} ${requests.length === 1 ? t("request") : t("requests")}`}
      maxHeight="90vh"
      onBack={() => navigate("/home")}
      showBackButton={true}
    >
      <div className="w-full space-y-4">
        {requests.map((request: any) => (
          <div
            key={request.request_id}
            onClick={() => navigateToRequestDetail(request)}
            className="p-5 rounded-xl border-2 border-gray-200 bg-white hover:shadow-lg transition-all cursor-pointer"
          >
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{getStatusIcon(request.status)}</span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getRequestTypeLabel(request.request_type)}
                  </h3>
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  {request.request_number && (
                    <p>
                      <span className="font-medium">{t("request_number")}:</span> {request.request_number}
                    </p>
                  )}
                  {(request.submitted_at || request.created_at) && (
                    <p>
                      <span className="font-medium">{t("submitted")}:</span>{' '}
                      {new Date(request.submitted_at || request.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div className={`px-3 py-1 rounded-lg border ${getStatusColor(request.status)} text-xs font-semibold whitespace-nowrap`}>
                {t(`status_${request.status}`) || request.status}
              </div>
            </div>

            {/* Quick action hint */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {request.status === 'draft'
                  ? t("tap_to_continue")
                  : request.status === 'preliminary_approved'
                  ? t("request_preliminary_approved_message")
                  : t("tap_to_view_details")}
              </span>
              <span className={isRTL ? "text-gray-400" : "text-gray-400"}>{isRTL ? "←" : "→"}</span>
            </div>
          </div>
        ))}
      </div>
    </ResponsiveFormLayout>
  );
};

export default RequestsListScreen;
